import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Plus, Pencil, Trash2, Check, X, RotateCcw, MessageSquarePlus, Bell, PartyPopper, Gift, Award } from "lucide-react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Empty, Modal, Uploader } from "@/components/atoms";

const TABS = ["Applications", "Members", "Offers", "Bookings", "Redeem", "Anniversary", "Emails", "Branding", "Invite"];
const OFFER_CATS = ["Tattoo", "Aftercare", "Merch", "Event"];
const BOOKING_STATUSES = ["pending", "contacted", "scheduled", "declined"];
const EMPTY_OFFER = { title: "", description: "", category: "Tattoo", discount_pct: 10, expiry: "", promo_code: "", image_url: "", is_event: false };

function Applications() {
  const [apps, setApps] = useState(null);
  const load = async () => { try { const { data } = await api.get("/admin/applications"); setApps(data); } catch (e) { setApps([]); } };
  useEffect(() => { load(); }, []);
  const act = async (uid, action) => {
    try { await api.post(`/admin/applications/${uid}/${action}`); toast.success(`Application ${action}d`); load(); }
    catch (e) { toast.error("Failed"); }
  };
  if (apps === null) return <Loading />;
  if (apps.length === 0) return <Empty title="No applications" />;
  return (
    <div className="flex flex-col gap-3">
      {apps.map((a) => (
        <div key={a.id} className="atc-card p-4" data-testid={`admin-app-${a.user_id}`}>
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-display font-bold">{a.full_name}</h3>
              <p className="font-mono2 text-[10px] text-[#554E40]">{a.email} · {a.city}</p>
            </div>
            <span className="border-2 border-black rounded px-2 py-1 font-mono2 text-[9px] uppercase bg-[#EAE4D3]">{a.status}</span>
          </div>
          <p className="text-sm mt-2">{a.reason}</p>
          <p className="font-mono2 text-[10px] text-[#554E40] mt-1">Heard via: {a.how_heard} · {a.phone}</p>
          <div className="flex gap-2 mt-3">
            {a.status !== "approved" && <button className="atc-btn btn-primary h-9 px-3 text-xs flex-1" onClick={() => act(a.user_id, "approve")} data-testid={`app-approve-${a.user_id}`}><Check size={14} /> Approve</button>}
            {a.status === "pending" && <button className="atc-btn btn-outline h-9 px-3 text-xs flex-1" onClick={() => act(a.user_id, "reject")} data-testid={`app-reject-${a.user_id}`}><X size={14} /> Reject</button>}
            {a.status === "approved" && <button className="atc-btn btn-outline h-9 px-3 text-xs flex-1" onClick={() => act(a.user_id, "revoke")} data-testid={`app-revoke-${a.user_id}`}><RotateCcw size={14} /> Revoke</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function MembersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [badgeInput, setBadgeInput] = useState({});
  const load = async () => { try { const { data } = await api.get("/admin/users"); setUsers(data); } catch (e) { setUsers([]); } };
  useEffect(() => { load(); }, []);
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.username || u.name || u.email}? This permanently removes their account, applications, bookings, perks and messages.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Member deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const act = async (u, action) => {
    try { await api.post(`/admin/applications/${u.id}/${action}`); toast.success(`Membership ${action}d`); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const addBadge = async (u) => {
    const label = (badgeInput[u.id] || "").trim();
    if (!label) return;
    try { await api.post(`/admin/users/${u.id}/badges`, { label, action: "add" }); setBadgeInput({ ...badgeInput, [u.id]: "" }); toast.success("Badge added"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const removeBadge = async (u, label) => {
    try { await api.post(`/admin/users/${u.id}/badges`, { label, action: "remove" }); load(); } catch (e) {}
  };
  if (users === null) return <Loading />;
  return (
    <div className="flex flex-col gap-3" data-testid="members-admin">
      <p className="text-sm text-[#554E40]">Manage every account — approve or revoke membership, hand out custom badges, and clear out stale members.</p>
      {users.map((u) => (
        <div key={u.id} className="atc-card p-4" data-testid={`admin-member-${u.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-display font-bold leading-tight truncate">{u.username || u.name}{u.role === "admin" ? " · staff" : ""}</p>
              <p className="font-mono2 text-[10px] text-[#554E40] truncate">{u.email}</p>
              <p className="font-mono2 text-[10px] text-[#554E40]">{u.membership_status}{u.approved_at ? ` · since ${new Date(u.approved_at).toLocaleDateString()}` : ""} · {u.referral_count || 0} refs</p>
            </div>
            {u.id === me.id && <span className="font-mono2 text-[9px] text-[#554E40] shrink-0">you</span>}
          </div>

          {(u.custom_badges || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {u.custom_badges.map((b) => (
                <span key={b} className="inline-flex items-center gap-1 border-2 border-black rounded px-2 py-1 font-mono2 text-[9px] uppercase" style={{ background: "#2A2118", color: "#C7A656" }}>
                  <Award size={11} /> {b}
                  <button onClick={() => removeBadge(u, b)} data-testid={`badge-remove-${u.id}`}><X size={11} /></button>
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <input className="atc-input flex-1 h-9 py-1 text-sm" placeholder="Custom badge (e.g. Founding Member)" value={badgeInput[u.id] || ""} onChange={(e) => setBadgeInput({ ...badgeInput, [u.id]: e.target.value })} data-testid={`badge-input-${u.id}`} />
            <button className="atc-btn btn-gold h-9 px-3 text-xs shrink-0" onClick={() => addBadge(u)} data-testid={`badge-add-${u.id}`}><Award size={13} /> Add</button>
          </div>

          {u.role !== "admin" && (
            <div className="flex gap-2 mt-2">
              {u.membership_status === "approved" ? (
                <button className="atc-btn btn-outline flex-1 h-9 text-xs" onClick={() => act(u, "revoke")} data-testid={`member-revoke-${u.id}`}><RotateCcw size={13} /> Revoke</button>
              ) : (
                <button className="atc-btn btn-outline flex-1 h-9 text-xs" onClick={() => act(u, "approve")} data-testid={`member-approve-${u.id}`}><Check size={13} /> Approve</button>
              )}
              {u.id !== me.id && (
                <button className="atc-btn btn-primary flex-1 h-9 text-xs" onClick={() => del(u)} data-testid={`member-del-${u.id}`}><Trash2 size={13} /> Delete</button>
              )}
            </div>
          )}
          {u.role === "admin" && u.id !== me.id && (
            <button className="atc-btn btn-primary w-full h-9 text-xs mt-2" onClick={() => del(u)} data-testid={`member-del-${u.id}`}><Trash2 size={13} /> Delete</button>
          )}
        </div>
      ))}
    </div>
  );
}

function OffersAdmin() {
  const [offers, setOffers] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = async () => { try { const { data } = await api.get("/offers"); setOffers(data); } catch (e) { setOffers([]); } };
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    const isEvent = !!editing.data.is_event;
    const payload = {
      ...editing.data,
      is_event: isEvent,
      discount_pct: isEvent ? 0 : (parseInt(editing.data.discount_pct) || 0),
      promo_code: isEvent ? "" : (editing.data.promo_code || ""),
      expiry: editing.data.expiry ? new Date(editing.data.expiry).toISOString() : null,
    };
    try {
      if (editing.mode === "edit") await api.put(`/admin/offers/${editing.data.id}`, payload);
      else await api.post("/admin/offers", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/offers/${id}`); load(); toast.success("Deleted"); } catch (e) {} };
  const upd = (k) => (e) => setEditing({ ...editing, data: { ...editing.data, [k]: e.target.value } });
  if (offers === null) return <Loading />;
  return (
    <div>
      <button className="atc-btn btn-gold w-full mb-4" onClick={() => setEditing({ mode: "add", data: { ...EMPTY_OFFER } })} data-testid="offer-new-btn"><Plus size={16} /> New Offer</button>
      <div className="flex flex-col gap-3">
        {offers.map((o) => (
          <div key={o.id} className="atc-card p-4 flex items-center justify-between gap-3" data-testid={`admin-offer-${o.id}`}>
            <div className="min-w-0">
              <p className="font-display font-bold leading-tight truncate">{o.title}</p>
              <p className="font-mono2 text-[10px] text-[#554E40]">{o.category} · {o.is_event || !o.discount_pct ? "Event" : `${o.discount_pct}% · ${o.promo_code || "auto codes"}`}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing({ mode: "edit", data: { ...o, expiry: o.expiry ? o.expiry.slice(0, 10) : "" } })} data-testid={`offer-edit-${o.id}`}><Pencil size={16} /></button>
              <button onClick={() => del(o.id)} data-testid={`offer-del-${o.id}`}><Trash2 size={16} color="#8B1E28" /></button>
            </div>
          </div>
        ))}
      </div>
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.mode === "edit" ? "Edit Offer" : "New Offer"} testid="offer-edit-modal">
        {editing && (
          <form onSubmit={save} className="flex flex-col gap-3">
            <input className="atc-input" placeholder="Title" value={editing.data.title} onChange={upd("title")} required data-testid="offer-f-title" />
            <textarea className="atc-input" rows={2} placeholder="Description" value={editing.data.description} onChange={upd("description")} required data-testid="offer-f-desc" />
            <select className="atc-input" value={editing.data.category} onChange={upd("category")} data-testid="offer-f-cat">{OFFER_CATS.map((c) => <option key={c}>{c}</option>)}</select>
            <label className="flex items-center gap-2 font-mono2 text-xs uppercase border-2 border-black rounded p-2 bg-[#F9F6F0]">
              <input type="checkbox" checked={!!editing.data.is_event} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, is_event: e.target.checked } })} data-testid="offer-f-event" />
              Members-only event (no discount)
            </label>
            {!editing.data.is_event && (
              <>
                <input className="atc-input" type="number" placeholder="Discount % (optional)" value={editing.data.discount_pct} onChange={upd("discount_pct")} data-testid="offer-f-discount" />
                <input className="atc-input" placeholder="Internal promo label (optional)" value={editing.data.promo_code} onChange={upd("promo_code")} data-testid="offer-f-code" />
              </>
            )}
            <label className="font-mono2 text-[10px] uppercase text-[#554E40]">Expiry</label>
            <input className="atc-input" type="date" value={editing.data.expiry} onChange={upd("expiry")} data-testid="offer-f-expiry" />
            {editing.data.image_url && <img src={fileUrl(editing.data.image_url)} alt="" className="w-full h-32 object-cover border-2 border-black rounded" />}
            <input className="atc-input" placeholder="Image URL" value={editing.data.image_url} onChange={upd("image_url")} data-testid="offer-f-image" />
            <Uploader onUploaded={(url) => setEditing({ ...editing, data: { ...editing.data, image_url: url } })} label="Or upload image" testid="offer-f-upload" />
            <button type="submit" className="atc-btn btn-primary" data-testid="offer-f-save">Save Offer</button>
          </form>
        )}
      </Modal>
    </div>
  );
}

function BookingsAdmin() {
  const [bookings, setBookings] = useState(null);
  const [active, setActive] = useState(null);
  const [dmOpen, setDmOpen] = useState(false);
  const [dmMsgs, setDmMsgs] = useState([]);
  const [dmText, setDmText] = useState("");
  const load = async () => { try { const { data } = await api.get("/admin/bookings"); setBookings(data); } catch (e) { setBookings([]); } };
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => { try { await api.put(`/admin/bookings/${id}`, patch); toast.success("Updated"); load(); if (active?.id === id) setActive({ ...active, ...patch }); } catch (e) { toast.error("Failed"); } };
  const delBooking = async (id) => { if (!window.confirm("Clear this booking request? This can't be undone.")) return; try { await api.delete(`/admin/bookings/${id}`); toast.success("Booking cleared"); load(); } catch (e) { toast.error("Failed"); } };
  const loadDm = async (id) => { try { const { data } = await api.get(`/dm/${id}`); setDmMsgs(data.messages); } catch (e) {} };
  const openDm = async (b) => { await api.post(`/admin/bookings/${b.id}/open-dm`); setActive(b); setDmOpen(true); loadDm(b.id); };
  const sendDm = async (e) => { e.preventDefault(); if (!dmText.trim()) return; const t = dmText; setDmText(""); await api.post(`/dm/${active.id}`, { text: t }); loadDm(active.id); };
  if (bookings === null) return <Loading />;
  if (bookings.length === 0) return <Empty title="No bookings" />;
  return (
    <div className="flex flex-col gap-3">
      {bookings.map((b) => (
        <div key={b.id} className="atc-card p-4" data-testid={`admin-booking-${b.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-display font-bold leading-tight">{b.tattoo_idea}</p>
              <p className="font-mono2 text-[10px] text-[#554E40]">{b.requester_name} · {b.style} · {b.size} · {b.placement}</p>
              {b.preferred_artist && <p className="font-mono2 text-[10px] text-[#554E40]">Artist: {b.preferred_artist}</p>}
              {b.budget && <p className="font-mono2 text-[10px] text-[#554E40]">Budget: {b.budget} · {b.preferred_dates}</p>}
            </div>
          </div>
          {b.reference_url && <img src={fileUrl(b.reference_url)} alt="ref" className="w-full h-32 object-cover border-2 border-black rounded mt-2" />}
          <select className="atc-input mt-3" value={b.status} onChange={(e) => update(b.id, { status: e.target.value })} data-testid={`booking-status-select-${b.id}`}>
            {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <textarea className="atc-input mt-2" rows={2} placeholder="Internal note" defaultValue={b.admin_note} onBlur={(e) => e.target.value !== b.admin_note && update(b.id, { admin_note: e.target.value })} data-testid={`booking-note-${b.id}`} />
          <button className="atc-btn btn-ink w-full mt-2 h-10 text-xs" onClick={() => openDm(b)} data-testid={`booking-dm-${b.id}`}><MessageSquarePlus size={14} /> Open DM With {b.requester_name}</button>
          <button className="atc-btn btn-outline w-full mt-2 h-9 text-xs" onClick={() => delBooking(b.id)} data-testid={`booking-clear-${b.id}`}><Trash2 size={13} /> Clear Request</button>
        </div>
      ))}
      <Modal open={dmOpen} onClose={() => setDmOpen(false)} title="Booking DM" testid="admin-dm-modal">
        {active && (
          <div className="flex flex-col h-[60vh]">
            <div className="flex-1 overflow-y-auto no-scrollbar flex flex-col gap-2 mb-3">
              {dmMsgs.length === 0 && <p className="text-sm text-[#554E40] text-center mt-6">Start the conversation.</p>}
              {dmMsgs.map((m) => (
                <div key={m.id} className={`max-w-[80%] border-2 border-black rounded-lg px-3 py-2 ${m.is_admin ? "bg-[#C7A656] self-end" : "bg-[#F9F6F0] self-start"}`}>
                  <p className="font-display font-bold text-[10px] mb-1">{m.author_name}</p>
                  <p className="text-sm">{m.text}</p>
                </div>
              ))}
            </div>
            <form onSubmit={sendDm} className="flex gap-2">
              <input className="atc-input flex-1" placeholder="Message..." value={dmText} onChange={(e) => setDmText(e.target.value)} data-testid="admin-dm-input" />
              <button type="submit" className="atc-btn btn-primary px-4" data-testid="admin-dm-send">Send</button>
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
}

function BrandingAdmin() {
  const { loadBranding } = useAuth();
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/branding").then(({ data }) => setForm(data)); }, []);
  const save = async () => { try { await api.put("/admin/branding", form); await loadBranding(); toast.success("Branding updated"); } catch (e) { toast.error("Failed"); } };
  if (!form) return <Loading />;
  const fields = [["login_hero", "Login Hero"], ["home_banner", "Home Banner"], ["profile_logo", "Parlor Logo"]];
  return (
    <div className="flex flex-col gap-5">
      {fields.map(([k, label]) => (
        <div key={k} className="atc-card p-4">
          <p className="font-display font-bold mb-2">{label}</p>
          {form[k] && <img src={fileUrl(form[k])} alt={label} className="w-full h-32 object-cover border-2 border-black rounded mb-2" />}
          <input className="atc-input mb-2" placeholder="Image URL" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`branding-${k}`} />
          <Uploader onUploaded={(url) => setForm({ ...form, [k]: url })} label="Upload" testid={`branding-upload-${k}`} />
        </div>
      ))}
      <button className="atc-btn btn-primary" onClick={save} data-testid="branding-save-btn">Save Branding</button>
    </div>
  );
}

function RedeemAdmin() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const load = async () => { try { const { data } = await api.get("/admin/redemptions"); setHistory(data); } catch (e) {} };
  useEffect(() => { load(); }, []);
  const redeem = async (e) => {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/redeem", { code });
      setResult(data);
      toast.success(`Redeemed for ${data.member.name}`);
      setCode("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-4" data-testid="redeem-admin">
      <p className="text-sm text-[#554E40]">Enter a member's perk/discount code to redeem it against their account. Each code redeems once.</p>
      <form onSubmit={redeem} className="flex gap-2">
        <input className="atc-input flex-1 font-mono2 uppercase" placeholder="e.g. WELCOME-AB12" value={code} onChange={(e) => setCode(e.target.value)} required data-testid="redeem-code" />
        <button type="submit" className="atc-btn btn-primary px-4" disabled={busy} data-testid="redeem-submit"><Gift size={16} /> Redeem</button>
      </form>
      {result && (
        <div className="atc-card p-4 bg-[#F4E9C9]" data-testid="redeem-result">
          <p className="font-display font-bold text-lg">Redeemed &#10003;</p>
          <p className="text-sm">{result.perk.title} ({result.perk.discount_pct}% off)</p>
          <p className="font-mono2 text-xs mt-1">Code {result.perk.code} · for <b>{result.member.name}</b>{result.member.email ? ` (${result.member.email})` : ""}</p>
          {result.was_expired && <p className="text-[#8B1E28] text-xs font-bold mt-1">Note: this code was past its expiry.</p>}
        </div>
      )}
      <div>
        <h3 className="font-display font-bold text-lg uppercase mb-2">Redemption History</h3>
        {history.length === 0 ? <p className="text-sm text-[#554E40]">No codes redeemed yet.</p> : (
          <div className="flex flex-col gap-2">
            {history.map((r) => (
              <div key={r.id} className="atc-card p-3" data-testid={`redemption-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono2 text-sm font-bold">{r.code}</p>
                    <p className="text-xs text-[#554E40]">{r.title} · {r.member_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono2 text-[10px] text-[#554E40]">{r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : ""}</p>
                    {r.redeemed_by && <p className="font-mono2 text-[9px] text-[#554E40]">by {r.redeemed_by}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmailsAdmin() {  const [t, setT] = useState(null);
  useEffect(() => { api.get("/admin/email-templates").then(({ data }) => setT(data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/admin/email-templates", t); toast.success("Email templates saved"); }
    catch (e) { toast.error("Failed"); }
  };
  if (!t) return <Loading />;
  return (
    <div className="flex flex-col gap-4" data-testid="emails-admin">
      <p className="text-sm text-[#554E40]">Edit the emails the club sends. Placeholders in curly braces are filled in automatically.</p>
      <div className="atc-card p-4">
        <p className="font-display font-bold mb-1">Welcome email (sent on approval)</p>
        <p className="font-mono2 text-[10px] text-[#554E40] mb-2">Placeholders: {"{name}"}, {"{referral_code}"}</p>
        <input className="atc-input mb-2" value={t.welcome_subject} onChange={(e) => setT({ ...t, welcome_subject: e.target.value })} data-testid="email-welcome-subject" />
        <textarea className="atc-input" rows={3} value={t.welcome_message} onChange={(e) => setT({ ...t, welcome_message: e.target.value })} data-testid="email-welcome-message" />
      </div>
      <div className="atc-card p-4">
        <p className="font-display font-bold mb-1">Admin booking alert (sent to all admins)</p>
        <p className="font-mono2 text-[10px] text-[#554E40] mb-2">Placeholders: {"{requester_name}"}, {"{tattoo_idea}"}, {"{style}"}, {"{size}"}, {"{placement}"}</p>
        <input className="atc-input mb-2" value={t.booking_subject} onChange={(e) => setT({ ...t, booking_subject: e.target.value })} data-testid="email-booking-subject" />
        <textarea className="atc-input" rows={3} value={t.booking_message} onChange={(e) => setT({ ...t, booking_message: e.target.value })} data-testid="email-booking-message" />
      </div>
      <div className="atc-card p-4">
        <p className="font-display font-bold mb-1">Session scheduled email (sent to member)</p>
        <p className="font-mono2 text-[10px] text-[#554E40] mb-2">Placeholders: {"{name}"}, {"{tattoo_idea}"}, {"{artist}"}, {"{note}"}</p>
        <input className="atc-input mb-2" value={t.scheduled_subject || ""} onChange={(e) => setT({ ...t, scheduled_subject: e.target.value })} data-testid="email-scheduled-subject" />
        <textarea className="atc-input" rows={3} value={t.scheduled_message || ""} onChange={(e) => setT({ ...t, scheduled_message: e.target.value })} data-testid="email-scheduled-message" />
      </div>
      <button className="atc-btn btn-primary" onClick={save} data-testid="email-save-btn">Save Templates</button>
    </div>
  );
}

function InviteAdmin() {  const [email, setEmail] = useState("");
  const invite = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/admin/invite", { email }); toast.success(data.promoted ? "User promoted to admin" : "Invite saved — promotes on signup"); setEmail(""); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <form onSubmit={invite} className="flex flex-col gap-3">
      <p className="text-sm text-[#554E40]">Invite an admin by email. If they already have an account they're promoted instantly; otherwise they become admin when they sign up.</p>
      <input className="atc-input" type="email" placeholder="admin@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="invite-email" />
      <button type="submit" className="atc-btn btn-primary" data-testid="invite-submit-btn">Send Invite</button>
    </form>
  );
}

function AnniversaryAdmin() {
  const [members, setMembers] = useState(null);
  const [years, setYears] = useState({});
  const load = async () => {
    try {
      const { data } = await api.get("/admin/members");
      setMembers(data);
      const y = {};
      data.forEach((m) => { y[m.id] = Math.max(1, m.years || 1); });
      setYears(y);
    } catch (e) { setMembers([]); }
  };
  useEffect(() => { load(); }, []);
  const trigger = async (m) => {
    try {
      const { data } = await api.post(`/admin/members/${m.id}/anniversary`, { year: parseInt(years[m.id]) || 1, force: true });
      toast.success(`Shout-out posted for ${m.name} · perk ${data.code}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (members === null) return <Loading />;
  if (members.length === 0) return <Empty title="No approved members yet" />;
  return (
    <div className="flex flex-col gap-3" data-testid="anniversary-admin">
      <p className="text-sm text-[#554E40]">Re-post a member's pinned ink-anniversary shout-out in Club Chat and (re)issue their 10% off perk. Pick the year, then re-trigger.</p>
      {members.map((m) => (
        <div key={m.id} className="atc-card p-4" data-testid={`anniv-member-${m.id}`}>
          <p className="font-display font-bold leading-tight">{m.name}{m.role === "admin" ? " · staff" : ""}</p>
          <p className="font-mono2 text-[10px] text-[#554E40]">{m.email}</p>
          <p className="font-mono2 text-[10px] text-[#554E40]">Member since {m.approved_at ? new Date(m.approved_at).toLocaleDateString() : "—"} · {m.years} yr · {m.anniversary_perks} anniversary perk(s)</p>
          <div className="flex items-center gap-2 mt-3">
            <label className="font-mono2 text-[10px] uppercase">Year</label>
            <input type="number" min={1} className="atc-input w-16 h-11 py-1 text-center" value={years[m.id] ?? 1} onChange={(e) => setYears({ ...years, [m.id]: e.target.value })} data-testid={`anniv-year-${m.id}`} />
            <button className="atc-btn btn-primary flex-1 h-11 text-xs" onClick={() => trigger(m)} data-testid={`anniv-trigger-${m.id}`}><PartyPopper size={14} /> Re-trigger</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Notifications() {  const [notes, setNotes] = useState([]);
  useEffect(() => {
    api.get("/admin/notifications").then(({ data }) => setNotes(data)).catch(() => {});
    api.post("/admin/notifications/read").catch(() => {});
  }, []);
  if (notes.length === 0) return null;
  return (
    <div className="atc-card p-3 mb-4 bg-[#F4E9C9]">
      <p className="font-mono2 text-[10px] uppercase tracking-widest mb-2 flex items-center gap-1"><Bell size={12} /> Recent Activity</p>
      <div className="flex flex-col gap-1 max-h-40 overflow-y-auto no-scrollbar">
        {notes.slice(0, 15).map((n) => (
          <p key={n.id} className="text-xs border-b border-black/20 pb-1">{n.text} <span className="text-[#554E40] font-mono2">· {new Date(n.created_at).toLocaleDateString()}</span></p>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Applications");
  useEffect(() => {
    if (user && user.role !== "admin") navigate("/", { replace: true });
  }, [user, navigate]);
  if (user?.role !== "admin") return null;
  return (
    <div data-testid="admin-page">
      <div className="px-5 pt-5 flex items-center gap-3">
        <button onClick={() => navigate("/")} data-testid="admin-back"><ChevronLeft size={22} /></button>
        <div>
          <span className="eyebrow">Parlor Control</span>
          <h1 className="font-display font-black text-2xl uppercase leading-none">Admin Console</h1>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-5 py-4">
        {TABS.map((t) => (
          <button key={t} className={`chip ${tab === t ? "chip-active" : ""}`} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>
      <div className="px-5 pb-8">
        {tab === "Applications" && <><Notifications /><Applications /></>}
        {tab === "Members" && <MembersAdmin />}
        {tab === "Offers" && <OffersAdmin />}
        {tab === "Bookings" && <BookingsAdmin />}
        {tab === "Redeem" && <RedeemAdmin />}
        {tab === "Anniversary" && <AnniversaryAdmin />}
        {tab === "Emails" && <EmailsAdmin />}
        {tab === "Branding" && <BrandingAdmin />}
        {tab === "Invite" && <InviteAdmin />}
      </div>
    </div>
  );
}
